/**
 * LandXmlScene.ts
 * -----------------------------------------------------------------------------
 * Turns a LandXML survey file into the same scene payload the C# exporter emits
 * (`BridgeGeometryPayload`), so Map/GIS ▸ Import Map draws its terrain and
 * alignments in the ordinary bridge viewport — the shared canvas, the shared
 * toolbar, the shared picking — instead of carrying a viewer of its own.
 *
 * The reader under `../landxml` and `../geom` is the standalone LandXML viewer's,
 * copied in unchanged so fixes flow between the two. This module is the only
 * seam: LandXML's world (native survey units, Z-up, TIN faces and CoordGeom
 * elements) in, the viewport's world (metres, Y-up, flat triangle soup) out.
 *
 * WHY ALIGNMENTS ARE RIBBONS AND NOT LINES
 * ========================================
 * `BridgeScene` only raycasts its merged surface mesh — "lines" parts are
 * batched by colour and carry no ids, so a centreline drawn as a line could not
 * be clicked and could not be highlighted by `select()`. Picking an alignment is
 * the entire point of this screen, so each one is emitted as a thin flat ribbon
 * of triangles carrying `id = alignment name`. Click-to-select, the orange
 * selection overlay and `select(ids)` from the alignment list then all work with
 * no change to BridgeScene. The ribbon also reads far better than a hairline
 * over a kilometre of terrain.
 *
 * WHAT THE ELEVATIONS ARE
 * =======================
 * Plan geometry in LandXML routinely carries no elevations at all. Where the
 * CoordGeom vertices have z it is used; otherwise the ribbon is draped onto the
 * terrain TIN, and failing that it lies flat at the terrain's lowest point. The
 * ribbon is lifted clear of the surface by a fraction of the model size so it
 * does not z-fight with the ground it is lying on. None of this reaches the
 * bridge: the geometry that gets imported is re-read from the file by
 * `xApp.ReadLandXMLFile`, and this is only what the user looks at while choosing.
 */

import type { BridgeGeometryPayload, BridgePart } from '../BridgeScene';
import {
  DEFAULT_TERRAIN, MAX_SUBDIVIDED_FACES, WIREFRAME_COLOR,
  affordableSubdivisions, shapeTerrain, wantedPasses, type TerrainDisplay,
} from './TerrainDisplay';
import { parseLandXml } from '../landxml/parse';
import type { LandXmlDoc } from '../landxml/types';
import { alignmentCoordGeom } from '../geom/alignpi';
import { tessellateCoordGeom } from '../geom/coordgeom';
import { TinIndex } from '../geom/tin';
import type { SurveyFrame } from './LandXmlGrid';

/** One alignment, as the Import Map panel lists it. Lengths/stations in metres. */
export interface LandXmlAlignmentInfo {
  /** Pick/selection key, and the alignment's position in the file's
   *  `<Alignments>` list — which is the order the C# reader walks, so it is what
   *  the host uses to check the right box on its ImportMapModel. Not the name:
   *  names repeat between alignments in real files and routinely carry trailing
   *  spaces, and neither survives being used as an identity. */
  key: string;
  /** Index behind `key`, so the host does not have to parse it back out. */
  index: number;
  /** The alignment's name exactly as written in the file — what the C# reader
   *  matches on. Show `label` instead; send this back unaltered. */
  name: string;
  /** `name` trimmed, or a placeholder when the file left it blank. For display. */
  label: string;
  desc?: string;
  /** Alignment length, metres. */
  length: number;
  staStart: number;
  staEnd: number;
  /** CoordGeom elements (lines, curves, spirals) behind it. */
  elements: number;
  /** The horizontal geometry was derived from `<AlignPI>` rather than read from
   *  `<CoordGeom>` — worth showing, because PI files are the ones that most
   *  often disagree between readers. */
  fromPIs: boolean;
  /** Every element joined end to end. A broken alignment still imports, but the
   *  bridge layout it produces will have a gap in it. */
  continuous: boolean;
  /** Carries at least one `<Profile>` with a vertical alignment. */
  hasProfile: boolean;
}

/** What the C# side needs to populate the panel beside the viewport. */
/** The elevation the surfaces span, for the legend. In the FILE's linear unit —
 *  the grid is labelled in survey coordinates, and a legend in metres beside a
 *  grid in feet would be its own small trap. */
export interface ElevationRange {
  min: number;
  max: number;
  /** The file's linear unit, e.g. "foot". */
  unit: string;
}

export interface LandXmlSummary {
  fileName?: string;
  /** Linear unit as the file declares it ("meter", "foot"). */
  units: string;
  /** Metric or imperial, from `<Units>`. */
  system: string;
  alignments: LandXmlAlignmentInfo[];
  surfaces: Array<{ name: string; faces: number }>;
  /** Present only when the surfaces are being shaded by height, and only when
   *  they actually span a range worth a legend. */
  elevation?: ElevationRange;
  /** Parser warnings plus anything this conversion had to say. Capped. */
  warnings: string[];
}

export interface LandXmlScene {
  payload: BridgeGeometryPayload;
  summary: LandXmlSummary;
  /** The parsed document, so the caller can rebuild with different display
   *  options without paying to read the file again. */
  doc: LandXmlDoc;
  /** How to get from the scene's metres back to the file's survey coordinates —
   *  what the map grid labels itself with. See io/LandXmlGrid.ts. */
  frame: SurveyFrame;
  /** Ground-plane height for the grid, in viewer metres: the model's lowest point. */
  groundY: number;
}

const TERRAIN_COLOR = '#8fa3b0';
const ALIGNMENT_COLOR = '#e8b23a';

/** Chord tolerance for arcs and spirals, as a fraction of the model diagonal.
 *  Curves are what the user recognises an alignment by, so this is tighter than
 *  the standalone viewer's fixed 10 mm at survey scale. */
const CHORD_TOLERANCE_FRACTION = 1 / 20000;

/**
 * Ribbon width as a fraction of the plan diagonal.
 *
 * Deliberately NOT a realistic carriageway width. At true scale a road across a
 * 2 km survey is about two pixels wide - which on an alignment-only file, with no
 * terrain behind it, reads as an empty canvas. The ribbon is the thing the user
 * has to see and click, so it is sized to be seeable and clickable: a diagonal
 * over 150 puts it around eight pixels across whatever the extent of the file.
 * The panel beside the view carries the real length and stationing.
 */
const RIBBON_WIDTH_FRACTION = 1 / 150;
const RIBBON_LIFT_FRACTION = 1 / 4000;

/** Width floor/ceiling in metres, so a 50 m footbridge survey and a 40 km
 *  motorway corridor both stay sane. */
const RIBBON_WIDTH_MIN = 2;
const RIBBON_WIDTH_MAX = 60;

const MAX_WARNINGS = 40;

/**
 * Display options for this screen. The terrain half is the shared
 * `TerrainDisplay`; Import Map adds nothing of its own today, but the alias keeps
 * the interop payload named for the screen that sends it.
 */
export type LandXmlDisplayOptions = TerrainDisplay;
export const DEFAULT_DISPLAY: LandXmlDisplayOptions = DEFAULT_TERRAIN;



/**
 * Parse `xml` and build the viewport payload plus the panel summary.
 * Throws only if the document cannot be parsed at all; a file with no
 * alignments still returns a scene (and an empty alignment list) so the user
 * can see that the terrain arrived and the alignments did not.
 */
export function buildLandXmlScene(
  xml: string, fileName?: string, opts: LandXmlDisplayOptions = DEFAULT_DISPLAY,
): LandXmlScene {
  const doc = parseLandXml(xml);
  if (fileName) doc.fileName = fileName;
  return buildSceneFromDoc(doc, opts);
}

/**
 * Rebuild the scene from an already-parsed document. Reading the XML is the
 * expensive half and none of the display options change it, so the toolbar's
 * Wireframe / Subdivide controls come back through here rather than re-parsing
 * a multi-megabyte survey on every click.
 */
export function buildSceneFromDoc(
  doc: LandXmlDoc, opts: LandXmlDisplayOptions = DEFAULT_DISPLAY,
): LandXmlScene {
  // The reader is shared with the standalone LandXML viewer, which draws a
  // web-map basemap under the model. There is no basemap here, so its advice to
  // go and pick a coordinate system is noise on this screen.
  const warnings = doc.warnings
    .filter((w) => !w.code.startsWith('crs.'))
    .slice(0, MAX_WARNINGS)
    .map((w) => w.message);
  const parts: BridgePart[] = [];

  // ---- model frame -------------------------------------------------------
  // Survey coordinates run to seven or eight figures; float32 vertex buffers
  // carry seven digits total, so everything is recentred before it is scaled.
  const b = doc.bounds;
  const has = Number.isFinite(b.minX) && Number.isFinite(b.minY);
  const ox = has ? (b.minX + b.maxX) / 2 : 0;
  const oy = has ? (b.minY + b.maxY) / 2 : 0;
  const oz = Number.isFinite(b.minZ) ? b.minZ : 0;
  const k = doc.units.linearToMeters || 1;

  // Plan diagonal in metres — the scale everything below is sized against.
  const diag = has ? Math.hypot(b.maxX - b.minX, b.maxY - b.minY) * k : 1000;

  const elevation = surfaceElevationRange(doc);
  const surfaces = buildSurfaces(doc, ox, oy, oz, k, parts, opts, warnings, elevation);
  const alignments = buildAlignments(doc, ox, oy, oz, k, diag, parts, warnings);

  return {
    payload: { units: 'm', colorBy: 'LandXML', parts },
    doc,
    frame: { ox, oy, k, unit: doc.units.linear },
    groundY: 0,
    summary: {
      fileName: doc.fileName,
      units: doc.units.linear,
      system: doc.units.system,
      alignments,
      surfaces,
      elevation: opts.elevation && elevation
        ? { min: elevation.lo, max: elevation.hi, unit: doc.units.linear }
        : undefined,
      warnings: warnings.slice(0, MAX_WARNINGS),
    },
  };
}

// ---------------------------------------------------------------------------
// Terrain
// ---------------------------------------------------------------------------

/**
 * Lowest and highest point of the TERRAIN, in survey units, or null when there is
 * no surface or it is dead flat.
 *
 * Surfaces only, not `doc.bounds`: the bounds take in every point, alignment and
 * pipe in the file, and one survey monument at a silly elevation would squash the
 * whole ramp into a couple of stops and flatten the ground it is meant to describe.
 */
function surfaceElevationRange(doc: LandXmlDoc): { lo: number; hi: number } | null {
  let lo = Infinity, hi = -Infinity;
  for (const s of doc.surfaces) {
    if (Number.isFinite(s.elevMin)) lo = Math.min(lo, s.elevMin);
    if (Number.isFinite(s.elevMax)) hi = Math.max(hi, s.elevMax);
  }
  if (!Number.isFinite(lo) || !Number.isFinite(hi) || hi - lo < 1e-9) return null;
  return { lo, hi };
}

/**
 * Each TIN becomes one non-indexed surface part. No id: the terrain is context,
 * not something the user is choosing, and leaving it out of `selectable` means a
 * stray click on the ground cannot clear or steal the alignment selection.
 */
function buildSurfaces(
  doc: LandXmlDoc, ox: number, oy: number, oz: number, k: number, parts: BridgePart[],
  opts: LandXmlDisplayOptions, warnings: string[],
  elevation: { lo: number; hi: number } | null,
): Array<{ name: string; faces: number }> {
  const listed: Array<{ name: string; faces: number }> = [];

  const faceCount = (s: LandXmlDoc['surfaces'][number]) => {
    let n = 0;
    for (let t = 0; t < s.triVisible.length; t++) if (s.triVisible[t]) n++;
    return n;
  };

  const wanted = wantedPasses(opts.subdivide);
  let totalFaces = 0;
  for (const s of doc.surfaces) totalFaces += faceCount(s);
  const passes = affordableSubdivisions(totalFaces, wanted);
  if (passes < wanted) {
    warnings.push(`Subdivision stopped at ${passes} of ${wanted} pass(es): `
      + `${totalFaces.toLocaleString()} faces across ${doc.surfaces.length} surface(s) `
      + `cannot go past the ${MAX_SUBDIVIDED_FACES.toLocaleString()} face ceiling.`);
  }

  for (const s of doc.surfaces) {
    const visible = faceCount(s);
    listed.push({ name: s.name, faces: visible });
    if (visible === 0) continue;

    const flat = new Float32Array(visible * 9);
    let i = 0;
    for (let t = 0, f = 0; f + 3 <= s.tris.length; t++, f += 3) {
      if (!s.triVisible[t]) continue;
      for (let c = 0; c < 3; c++) {
        const v = s.tris[f + c] * 3;
        flat[i++] = (s.xyz[v] - ox) * k;
        flat[i++] = (s.xyz[v + 2] - oz) * k;
        flat[i++] = -(s.xyz[v + 1] - oy) * k;
      }
    }

    const shaped = shapeTerrain(
      flat, passes, opts,
      elevation ? (elevation.lo - oz) * k : 0,
      elevation ? (elevation.hi - oz) * k : 0,
    );

    parts.push({
      name: s.name,
      color: TERRAIN_COLOR,
      opacity: 1,
      kind: 'surface',
      positions: Array.from(shaped.positions),
      vcolors: shaped.vcolors,
    });

    if (shaped.edges) {
      parts.push({
        name: `${s.name} (edges)`,
        color: WIREFRAME_COLOR,
        opacity: 1,
        kind: 'lines',
        positions: Array.from(shaped.edges),
      });
    }
  }

  return listed;
}

// ---------------------------------------------------------------------------
// Alignments
// ---------------------------------------------------------------------------

function buildAlignments(
  doc: LandXmlDoc, ox: number, oy: number, oz: number, k: number, diag: number,
  parts: BridgePart[], warnings: string[],
): LandXmlAlignmentInfo[] {
  const listed: LandXmlAlignmentInfo[] = [];
  if (!doc.alignments.length) return listed;

  const halfWidth = Math.min(RIBBON_WIDTH_MAX, Math.max(RIBBON_WIDTH_MIN, diag * RIBBON_WIDTH_FRACTION)) / 2;
  const lift = diag * RIBBON_LIFT_FRACTION;
  const tolerance = (diag * CHORD_TOLERANCE_FRACTION) / k;   // back to model units

  // Built once for the whole file, not per alignment: a TIN index is the
  // expensive part of draping and every alignment drapes onto the same ground.
  const indexes = doc.surfaces.map((s) => new TinIndex(s));
  // The HIGHEST surface over this point, not the first one that covers it. A file
  // routinely carries both existing and finished grade, and an alignment belongs on
  // top of the ground, not buried under the layer that was laid over it - in plan,
  // where an alignment is picked, being under a surface means being invisible.
  const groundAt = (x: number, y: number): number | undefined => {
    let top: number | undefined;
    for (const idx of indexes) {
      const z = idx.elevationAt(x, y);
      if (z !== undefined && (top === undefined || z > top)) top = z;
    }
    return top;
  };

  for (let ai = 0; ai < doc.alignments.length; ai++) {
    const a = doc.alignments[ai];
    const key = `align:${ai}`;
    const label = a.name.trim() || `(unnamed alignment ${ai + 1})`;
    const conv = alignmentCoordGeom(a);
    const tess = tessellateCoordGeom(conv.coordGeom, {
      tolerance,
      staStart: a.staStart,
      lookup: (name) => {
        const p = doc.cgPointsByName.get(name);
        return p ? { x: p.x, y: p.y, z: p.z } : undefined;
      },
    });

    let vertices = 0;
    for (const run of tess.runs) {
      if (run.length < 2) continue;
      vertices += run.length;
      parts.push({
        name: label,
        id: key,
        color: ALIGNMENT_COLOR,
        opacity: 1,
        kind: 'surface',
        positions: ribbon(run, ox, oy, oz, k, halfWidth, lift, groundAt),
      });
    }

    if (vertices === 0) {
      warnings.push(`Alignment "${label}" has no horizontal geometry that could be drawn.`);
    }
    for (const w of tess.warnings.slice(0, 5)) warnings.push(`Alignment "${label}": ${w}`);
    for (const w of conv.warnings.slice(0, 5)) warnings.push(`Alignment "${label}": ${w}`);

    const length = (tess.totalLength || a.length || 0) * k;
    const staStart = a.staStart * k;
    listed.push({
      key,
      index: ai,
      name: a.name,
      label,
      desc: a.desc,
      length,
      staStart,
      staEnd: staStart + length,
      elements: conv.coordGeom.elements.length,
      fromPIs: conv.fromPIs,
      continuous: tess.continuous,
      hasProfile: a.profiles.some((p) => p.aligns.length > 0),
    });
  }

  return listed;
}

/**
 * Two triangles per centreline span, offset ±`halfWidth` normal to the run in
 * plan. The offset direction at a vertex is the average of the directions of the
 * segments either side of it, so the ribbon keeps a constant width round curves
 * instead of pinching at every tessellation vertex.
 */
function ribbon(
  run: Array<{ x: number; y: number; z?: number; sta: number }>,
  ox: number, oy: number, oz: number, k: number,
  halfWidth: number, lift: number,
  groundAt: (x: number, y: number) => number | undefined,
): number[] {
  const n = run.length;

  // Left/right edge vertices in viewer space, built first so the triangles below
  // are pure indexing.
  const lx = new Float64Array(n), ly = new Float64Array(n), lz = new Float64Array(n);
  const rx = new Float64Array(n), ry = new Float64Array(n), rz = new Float64Array(n);

  for (let i = 0; i < n; i++) {
    const p = run[i];
    const prev = run[i > 0 ? i - 1 : 0];
    const next = run[i < n - 1 ? i + 1 : n - 1];

    let dx = next.x - prev.x;
    let dy = next.y - prev.y;
    const len = Math.hypot(dx, dy);
    if (len > 1e-12) { dx /= len; dy /= len; } else { dx = 1; dy = 0; }

    // Plan normal, in model units — halfWidth is metres, so divide by k.
    const half = halfWidth / k;
    const nx = -dy * half;
    const ny = dx * half;

    const z = (p.z ?? groundAt(p.x, p.y) ?? oz);
    const vy = (z - oz) * k + lift;

    lx[i] = (p.x + nx - ox) * k; ly[i] = vy; lz[i] = -(p.y + ny - oy) * k;
    rx[i] = (p.x - nx - ox) * k; ry[i] = vy; rz[i] = -(p.y - ny - oy) * k;
  }

  const out = new Array<number>((n - 1) * 18);
  let o = 0;
  for (let i = 0; i + 1 < n; i++) {
    const j = i + 1;
    // left[i], right[i], right[j]
    out[o++] = lx[i]; out[o++] = ly[i]; out[o++] = lz[i];
    out[o++] = rx[i]; out[o++] = ry[i]; out[o++] = rz[i];
    out[o++] = rx[j]; out[o++] = ry[j]; out[o++] = rz[j];
    // left[i], right[j], left[j]
    out[o++] = lx[i]; out[o++] = ly[i]; out[o++] = lz[i];
    out[o++] = rx[j]; out[o++] = ry[j]; out[o++] = rz[j];
    out[o++] = lx[j]; out[o++] = ly[j]; out[o++] = lz[j];
  }
  return out;
}
